import { createContext, useContext } from 'react'

import { WorkflowManifestFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import { EdgeUI, NodeUI } from 'modules/workflows/components/Node/types'
import { NodeSidepanelSchema } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { BuildingBlock } from 'modules/workflows/components/WorkflowBuildingBlocksBox'

export type TriggerConfig = { title: string; description: string }

export type HandlerFormComponentPayload = { [key: string]: unknown }
export type HandlerFormComponent = {
  payload: HandlerFormComponentPayload
  onChange: (values: HandlerFormComponentPayload, isValid: boolean) => void
}

export type HandlerForms = {
  [handlerName: string]: {
    dropDownLabel: string
    component: React.ComponentType<HandlerFormComponent>
    initializer: (payload: HandlerFormComponentPayload) => { [key: string]: unknown }
    beforeSave?: (
      payload: HandlerFormComponentPayload,
      handlerType: string
    ) => { [key: string]: unknown }
    schema: z.ZodSchema<NodeSidepanelSchema>
  }
}

export type MenuControls = {
  closeAllMenus: () => void
  registerMenu: (id: string, closeCallback: () => void) => () => void
}

type WorkflowContextProps = {
  buildingBlocks: BuildingBlock[]
  nodesUI: NodeUI[]
  setNodesUI: React.Dispatch<React.SetStateAction<NodeUI[]>>
  edgesUI: EdgeUI[]
  setEdgesUI: React.Dispatch<React.SetStateAction<EdgeUI[]>>
  isDirty: boolean
  elevateNodeZIndex: (nodeId: string) => void
  restoreNodeZIndex: (nodeId: string) => void
  triggerConfig: TriggerConfig
  workflowManifest: WorkflowManifestFragment
  handlerForms: HandlerForms
  menuControls: MenuControls
  sidepanelDirty: boolean
  setSidepanelDirty: (dirty: boolean) => void
}

export const WorkflowContext = createContext<WorkflowContextProps>({
  buildingBlocks: [],
  nodesUI: [],
  edgesUI: [],
  isDirty: false,
  sidepanelDirty: false,
  setSidepanelDirty: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  elevateNodeZIndex: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  restoreNodeZIndex: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  triggerConfig: {
    title: 'Trigger',
    description: 'This is the starting point of your useCase',
  },
  workflowManifest: {
    useCase: 'generic',
    workflowDataInputs: [],
    handlers: [],
  },
  setNodesUI: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  setEdgesUI: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  handlerForms: {},
  menuControls: {
    closeAllMenus: () => {
      throw new Error('WorkflowContext not properly initialized')
    },
    registerMenu: () => {
      throw new Error('WorkflowContext not properly initialized')
    },
  },
})

export const useWorkflowContext = () => useContext(WorkflowContext)
